/**
 * 连线驱动的生成模式同步：上游「有无图片输入」翻转时自动切换默认模式。
 *
 * 规则（只在 hasImageUpstream 布尔翻转时触发，即连线结构变化；上游文字等
 * 内容变化不重算，避免覆盖用户连线后手动改选的模式）：
 *  - 连入图片：文生模式 → 图生模式；手动选定的局部重绘 / 首尾帧等依赖图
 *    的精调模式保留不动
 *  - 断开全部图片：依赖图片输入的模式回落文生（无图时这些模式无法成立）
 *
 * 模型不支持目标模式时不切换；能力未知（模型未加载）时由调用方传 true
 * 乐观放行，由各节点已有的模型能力修正逻辑兜底。
 */
import { useEffect } from 'react';
import { boardActions, getBoardState } from '../state/boardStore';

export interface UpstreamModeSyncOptions {
  nodeId: string;
  /** 上游是否提供了图片输入（与 aggregateUpstream.referenceImages 口径一致） */
  hasImageUpstream: boolean;
  /** 无图时的默认模式（text2img / text2video） */
  textMode: string;
  /** 有图时的默认模式（img2img / img2video） */
  imgMode: string;
  /** 依赖图片输入的模式（无图时回落 textMode） */
  imageDependentModes: string[];
  /** 图生模式当前是否可用（模型能力未知时传 true） */
  imgModeAvailable: boolean;
  /** 文生模式当前是否可用（模型能力未知时传 true） */
  textModeAvailable: boolean;
}

export function useUpstreamModeSync(opts: UpstreamModeSyncOptions): void {
  const { nodeId, hasImageUpstream } = opts;

  useEffect(() => {
    // 只读预览：模式跟随快照原样展示，不做连线驱动的自动切换
    if (getBoardState().readOnly) return;
    // 模式从 store 现取：既避免闭包旧值，也避免把 currentMode 列进依赖后
    // 用户手动改选会立即触发重跑、把刚选的模式又翻回去（本 hook 只做边沿触发）
    const node = getBoardState().nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const current =
      ((node.data as Record<string, unknown>).mode as string | undefined) ?? opts.textMode;

    if (hasImageUpstream) {
      if (current === opts.textMode && opts.imgModeAvailable) {
        boardActions.updateNodeData(nodeId, { mode: opts.imgMode });
      }
    } else if (opts.imageDependentModes.includes(current) && opts.textModeAvailable) {
      boardActions.updateNodeData(nodeId, { mode: opts.textMode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, hasImageUpstream]);
}
